import {defineComponent} from "vue";
import ESymbol from "@/enums/ESymbol";

export default defineComponent({
    methods: {
        /**
         * Convert given number to german locale formatted string, e.g. 123456.78 -> 123.456,78
         * It is also possible to append a suffix like € or %.
         * This is the "main" function which will be used in other functions.
         * @param number
         * @param digits
         * @param suffix
         * @param separator
         * @return String(HTML) Returns HTML if parameter separator equals ESymbol.SPACE_HTML
         */
        convertNumber: function (number: number, digits = 0, suffix = "", separator = ""): string {
            return number.toLocaleString('de-DE', {minimumFractionDigits: digits, maximumFractionDigits: digits}) + separator + suffix;
        },
        /**
         * Convert given number to german locale formatted string with additional given suffix.
         * It is also possible to define a custom separator between number and suffix. Default is &nbsp; (HTML)
         * This function will be called inside other function with predefined suffixes.
         * @param number
         * @param suffix
         * @param separator
         * @return String(HTML) Returns HTML if parameter separator is not given, otherwise it depends on separator value
         */
        convertNumberWithSuffix: function (number: number, suffix: string, separator: string = ESymbol.SPACE_HTML): string {
            return this.convertNumber(number, undefined, suffix, separator);
        },
        /**
         * Convert given number to german locale formatted string with suffix €.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithSuffixEuro: function (number: number): string {
            return this.convertNumberWithSuffix(number, ESymbol.EURO);
        },
        /**
         * Convert given number to german locale formatted string with suffix EUR.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithSuffixEuroIso: function (number: number): string {
            return this.convertNumberWithSuffix(number, ESymbol.EURO_ISO);
        },
        /**
         * Convert given number to german locale formatted string with suffix EUR.
         * This function returns a string, no HTML.
         * @param number
         * @return String
         */
        convertNumberWithSuffixEuroIso: function (number: number): string {
            return this.convertNumberWithSuffix(number, ESymbol.EURO_ISO, ESymbol.SPACE);
        },
        /**
         * Convert given number to german locale formatted string with suffix %.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithSuffixPercent: function (number: number): string {
            return this.convertNumberWithSuffix(number, ESymbol.PERCENT);
        },
        /**
         * Convert given number to german locale formatted string with digits.
         * @param number
         * @param digits
         * @return String
         */
        convertNumberWithDigits: function (number: number, digits = 2): string {
            return this.convertNumber(number, digits);
        },
        /**
         * Convert given number to german locale formatted string with digits and additional given suffix.
         * It is also possible to define a custom separator between number and suffix. Default is &nbsp; (HTML)
         * This function will be called inside other function with predefined suffixes.
         * @param number
         * @param suffix
         * @param digits
         * @param separator
         * @return String(HTML) Returns HTML if parameter separator is not given, otherwise it depends on separator value
         */
        convertNumberWithDigitsAndSuffix: function (number: number, suffix: string, digits = 2, separator: string = ESymbol.SPACE_HTML): string {
            return this.convertNumber(number, digits, suffix, separator);
        },
        /**
         * Convert given number to german locale formatted string with two digits (default) and suffix €.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithTwoDigitsAndSuffixEuro: function (number: number): string {
            return this.convertNumberWithDigitsAndSuffix(number, ESymbol.EURO);
        },
        /**
         * Convert given number to german locale formatted string with two digits (default) and suffix EUR.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithTwoDigitsAndSuffixEuroIso: function (number: number): string {
            return this.convertNumberWithDigitsAndSuffix(number, ESymbol.EURO_ISO);
        },
        /**
         * Convert given number to german locale formatted string with two digits (default) and suffix EUR.
         * This function returns a string, no HTML.
         * @param number
         * @return String
         */
        convertNumberWithTwoDigitsAndSuffixEuroIso: function (number: number): string {
            return this.convertNumberWithDigitsAndSuffix(number, ESymbol.EURO_ISO, undefined, ESymbol.SPACE);
        },
        /**
         * Convert given number to german locale formatted string with two digits (default) and suffix %.
         * @param number
         * @return String(HTML)
         */
        convertNumberToHtmlWithTwoDigitsAndSuffixPercent: function (number: number): string {
            return this.convertNumberWithDigitsAndSuffix(number, ESymbol.PERCENT);
        }
    }
});
